import { createEventEmitter, EventEmitter } from "./utils";
import { context } from "./context";
import { workflow } from "./workflow";
import { appStore } from "./app-store";
import { auth } from "./auth";
import { sso } from "./sso";
import { AppInfo, UnmountFunc, UserMessageType, UserMessages, jituxDashboardAppId, loginAppId } from "../models";
import { messageHandler } from "./message-handler";
import Cookies from 'js-cookie';
import push from './push';
import eventHandlerManager from 'cov-jitux-push-event-handler';
import Config from "./config";
import HeapAnalytics from 'cov-jitux-heap';

class Shell {
  private _currentApp: AppInfo = null;
  private _appOpenedEvent: EventEmitter = createEventEmitter();
  private _lastAppMountDomId: string = "AppMount1";
  private _unmounts: { [appId: string]: UnmountFunc } = {};
  private _baseHref: string;
  private _heapAnalytics: HeapAnalytics;
  private _heapReady: boolean = false;
  private _userReady: boolean = false;
  private _trackEventDelayMs: number = 1000;

  private get version(): string {
    return "0.1";
  }

  private get theme(): string {
    const params = new URLSearchParams(window.location.search);
    const host = window.location.host;
    const enlyteTheme = "Enlyte";
    return params.get("theme") || context.get("theme") || (host?.toLowerCase().indexOf(enlyteTheme.toLowerCase()) >= 0 ? enlyteTheme : "");
  }

  get apps(): AppInfo[] {
    return appStore.apps;
  }

  set currentApp(value: AppInfo) {
    this._currentApp = value;
  }

  get appOpenedEvent(): EventEmitter {
    return this._appOpenedEvent;
  }

  start(): void {
    const tryStart = () => {
      const path = window.location.pathname;
      if (path?.endsWith("/logout")) {
        this.signOut();
      }
      if (window.opener != null && window.name === "ReloginPopup") {
        console.log("In Popup");
        const params = new URLSearchParams(window.location.search);
        Config.loadConfig().then(_ => {
          auth.authenticateAuthCodePromise(params.get("code"))
            .then(() => {
              window.close();
            });
        });
      } else {
        Config.loadConfig().then(async _ => {
          if ( !auth.isUserSignedOut ) {
            await this.initializeHeapAnalytics();
          }
          auth.setOauth();          
          appStore
            .loadAppSettings()
            .then(_ => {
              this.initialize();
              this.signIn();
            })
            .catch(error => {
              this.trackError("AppSettingsLoadError", { error: error.toString() });

              messageHandler.handle({
                type: UserMessageType.Error,
                message: UserMessages.UNEXPECTED_ERROR,
                detail: `Unable to initialize settings.\n\r${error}`,
                handleRetry: () => new Promise<void>(resolve => resolve(tryStart()))
              });
            });
        });
      }
    };

    this.addGlobalErrorHandlers();
    window['sso'] = sso.navigateProvider;
    window['ssoCustom'] = sso.navigateUrl;
    window['signOut'] = () => this.signOut();

    tryStart();
  }

  private async initializeHeapAnalytics(): Promise<void> {
    try {
      if (this._heapAnalytics) {
        this._heapReady = true;        
        return;
      }

      const heapEnabled = Config.get("heapEnabled");
      console.log('Heap Analytics Enlyte360 enabled - ' + heapEnabled);
      if (heapEnabled !== "true") {
        console.log('Heap Analytics is disabled at Enlyte360 level');
        return;
      }

      const clientConfigUrl = Config.get("clientConfigUrl");
      if (!clientConfigUrl) {
        console.log('Client config URL not found, skipping Heap Analytics initialization');
        return;
      }

      const response = await fetch(clientConfigUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch client config: ${response.status}`);
      }

      const config = await response.json();
      if (!config || (config.heap !== undefined && config.heap !== "enabled")) {
        console.log('Heap Analytics is disabled in client configuration');
        return;
      }

      const pathName = window.location.pathname;
      const apps = appStore.apps;
      const app = apps.find(x =>
        this.matchPath(x.baseRoute, pathName) &&
        context.hasItems(x.triggerContexts)
      );

      if (app?.appConfigurations?.heapEnabled === "false") {
        console.log('Heap Analytics is disabled at the application level');
        return;
      }

      const heapAppId = app?.appConfigurations?.heapAppId || Config.get("heapAppId");
      if (!heapAppId) {
        console.log('Heap App ID not found, skipping Heap Analytics initialization');
        return;
      }

      const isDev = process.env.NODE_ENV !== 'production';
      this._heapAnalytics = new HeapAnalytics({
        appId: heapAppId,
        development: isDev,
        disableTextCapture: true
      });

      await this._heapAnalytics.initialize();
      console.log('Heap Analytics initialized successfully');
      this._heapReady = true;
      try {
        if ( auth.userInfo ) {
          const userInfo = auth.userInfo;
          const portalUserInfo = userInfo?.portalUserInfo;
          auth.identifyUserForAnalytics(auth.userInfo, portalUserInfo)
        }
      } catch (e) {
         console.log('Heap Analytics: Unable to identify user from context');
      }
    } catch (e) {
      console.log('Heap Analytics: Initialization failed -', e);
    }
  }  
  

  private trackEvent(eventName: string, properties: any = {}): void {
    try {
      if (!this._heapAnalytics?.trackEvent) return;
      this._heapAnalytics.trackEvent(eventName, properties);
    } catch (e) {
      console.log(`Heap Analytics: Failed to track event "${eventName}" -`, e);
    }
  }

  private trackError(errorType: string, errorDetails: any = {}): void {
    try {
      if (!this._heapAnalytics?.trackEvent) return;
      this._heapAnalytics.trackEvent('Error', {
        errorType,
        ...errorDetails,
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.log(`Heap Analytics: Failed to track error "${errorType}" -`, e);
    }
  }

  getRelatedApps(): Array<AppInfo> {
    return this.apps.filter(
      x =>
        this._currentApp && x.id !== this._currentApp.id && x.id !== jituxDashboardAppId && x.alive && context.hasItems(x.triggerContexts)
    );
  }

  private addGlobalErrorHandlers() {
    window.addEventListener("error", (e: ErrorEvent) => {
      this.trackError("GlobalError", {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno
      });
      this.handleUnexpectedError(`${e.message} at ${e.filename} in ${e.lineno}, ${e.colno}`);
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      this.trackError("UnhandledRejection", {
        reason: event.reason?.toString() || "Unknown reason"
      });
      this.handleUnexpectedError(event.reason.stack);
    });
  }

  private initialize(): void {
    context.set("version", this.version);
    context.set("theme", this.theme);

    context.changedEvent.subscribe((key: string, value: any) => {
      if (key === "identity" && value !== null) {
        try {
          if ( auth.userInfo ) {
            const userInfo = auth.userInfo;
            const portalUserInfo = userInfo?.portalUserInfo;
            auth.identifyUserForAnalytics(auth.userInfo, portalUserInfo)
          } 
        } catch (e) {
          console.log('Heap Analytics: Failed to reset identity on token refresh - ', e);
        }
      }

      if (key === "identity" && value === null) {
        this.trackEvent("UserSignedOut");

        try {
          if (this._heapAnalytics) {
            this._heapAnalytics.resetIdentity();
          }
        } catch (e) {
          console.log('Heap Analytics: Failed to reset identity on sign out -', e);
        }

        // Reset heap/user ready flags on sign out
        this._heapReady = false;
        this._userReady = false;

        if (localStorage.getItem("suppress_jitux_ad_signout") !== "true") localStorage.removeItem("jitux_ad_signedin");

        if (auth.isSsoAuthMode) {
          if (!auth.hasAuthCode) {
            messageHandler.handle({
              type: UserMessageType.Custom,
              title: "Logged Out",
              message: "You have logged out of enlyte360. Please close all enlyte360 browser tabs and start a new session by clicking on your company's internal Enlyte SSO link.",
              hideClose: true,
            });
          }
        }
        else {
          if (!auth.hasAuthCode) {
            console.info("Navigating to post login redirect due to identity event with missing token.");
            window.location.href = auth.postLoginRedirect ?? this._baseHref;
          }
        }
      }
    });

    context.actionEvent.subscribe((action: string, args: { appId: string }) => {
      if (action === "openApp") {
        this.openApp(args.appId);
      }
    });

    workflow.navigateEvent.subscribe((appId: any) => {
      this.openApp(appId);
    });

    const baseTag = document.querySelector("base");
    this._baseHref = baseTag ? baseTag.getAttribute("href") : "/";

    window.addEventListener("popstate", _ => {
      this.navigate(window.location.pathname, true);
    });
  }

  initPush(app: AppInfo) {
    if (app?.appConfigurations?.applicationCode) {
      let identity = auth.userInfo;
      let orgId = identity?.organizationId ? identity?.organizationId : identity?.company_code;
      let appCode = app?.appConfigurations?.applicationCode;
      push.init(orgId, appCode).then(registration => {
        if (registration) {
          eventHandlerManager.addEventHandler('defaultHandler', 'enlyte360', "message", (notification) => {
            messageHandler.handle({
              type: UserMessageType.Custom,
              title: notification.title,
              message: notification.description,
              hideClose: false,
            });
          });
          push.subscribe(registration);
        }
      });
    }
  }

  matchPath(appBaseRoute: string, path: string): boolean {
    const baseRoute = this._baseHref + appBaseRoute;
    return path === baseRoute || path === baseRoute + "/" || path.startsWith(baseRoute + "/");
  }

  mount(app: AppInfo): Promise<void> {
    const lastAppMountDomId = this._lastAppMountDomId;
    const appMountDomId = lastAppMountDomId === "AppMount1" ? "AppMount2" : "AppMount1";
    this._lastAppMountDomId = appMountDomId;

    return new Promise((resolve: () => void) => {
      const prevApp = this._currentApp;
      if (prevApp) this.unmount(prevApp);
      this._currentApp = app;

      this._unmounts[app.id] = app.mount(appMountDomId, context);

      document.getElementById(appMountDomId).classList.remove("hide");
      document.getElementById(lastAppMountDomId).classList.add("hide");

      resolve();
    });
  }

  navigate(pathName: string, isBrowserHistoryEvent: boolean = false): void {
    auth.configureLoginPopup();
    if (pathName == this._baseHref) this.openMainMenu();
    else {
      const app = this.apps.find(x => this.matchPath(x.baseRoute, pathName) && context.hasItems(x.triggerContexts));
      if (app) {
        if (this._currentApp !== app) this.openApp(app.id, isBrowserHistoryEvent);
      } else this.openMainMenu();
    }
  }

  openMainMenu(): void {
    if (this._currentApp) this.unmount(this._currentApp);
    this._currentApp = null;

    if (new URLSearchParams(window.location.search).has("dashboard")) {
      this.openApp(jituxDashboardAppId);
      return;
    }

    let relayState = auth.relayState;
    let params = new URLSearchParams(window.location.search);
    if (!relayState && params.has("relay_state")) relayState = params.get("relay_state");;
    if (relayState && auth.disableRelayState) auth.removeRelayState();
    if (relayState && !auth.disableRelayState) {
      try {
        let url = relayState.indexOf("://") > 0 ? new URL(relayState) : new URL(relayState, window.location.origin);
        let relayStateAppId = url.searchParams.get("providerId");
        const app = this.apps.find(x => x.appConfigurations?.applicationCode === relayStateAppId && context.hasItems(x.triggerContexts));
        if (app?.appConfigurations?.cardMode === "sso") {
          auth.removeRelayState();
          window['sso'](relayStateAppId, false, relayState);
        } else {
          auth.removeRelayState();
          window.location.href = url.href, false;
          return;
        }
      } catch (e) {
        console.log(e);
        this.trackError("RelayStateError", { error: e.toString() });
      }
    }

    const apps = this.apps.filter(a =>
      a.id !== jituxDashboardAppId &&
      Number(a.appConfigurations?.clientDashboardId ?? 0) === appStore.clientDashboardId &&
      context.hasItems(a.triggerContexts)
    );
    const openApp = (apps.length === 1 &&
      apps[0].resourceBaseUrl &&
      apps[0].appConfigurations?.autoNavigate !== "false");

    if (openApp) {
      // Only the Auto navigate event waits a short delay so the beacon has time
      // to send before we start (potentially blocking) app mount work.
      this.trackEvent("Auto navigate " + apps[0].id, {
        appId: apps[0].id,
        appName: apps[0].displayName || apps[0].id,
        path: window.location.pathname
      });
      setTimeout(() => this.openApp(apps[0].id), this._trackEventDelayMs);
    } else {
      this.openApp(jituxDashboardAppId);
    }
  }

  openApp(id: string, isBrowserHistoryEvent: boolean = false): void {
    const app = this.apps.find(x => x.id === id);

    this.initPush(app);

    this.getAppTokenForApp(app);
    if (!app) {
      this.trackError("AppNotFound", { appId: id });
      this.handleUnexpectedError(`Application with id ${id} cannot be opened as it is not found`);
      return;
    }

    this.showLoading(true);

    window.sessionStorage.setItem("appId", JSON.stringify(app));

    const open = (app: AppInfo) => {
      this.mount(app)
        .then((_: any) => {
          this.showRelatedAppsBar(app);
          this.showLoading(false);

          const currentPath = window.location.pathname;
          if (!this.matchPath(app.baseRoute, currentPath))
            this.saveToBrowserHistory(app.lastVisitedRoute || app.baseRoute, isBrowserHistoryEvent);
          this.appOpenedEvent.emit(this._currentApp);          
        })
        .catch(error => {
          this.trackError("AppMountError", {
            appId: app.id,
            error: error.toString()
          });
          this.handleErrorOpeningApp(error.stack);
        });
    };

    setTimeout(() => {
      if (!app.module)
        appStore
          .loadApp(app)
          .then((app: any) => open(app))
          .catch((err: any) => {
            this.trackError("AppLoadError", {
              appId: app.id,
              bundleUrl: app.bundleUrl,
              error: err.toString()
            });
            this.handleErrorOpeningApp(`App '${app.id}' (${app.bundleUrl}) failed to load. Error:  ${err.stack}`);
          });
      else open(app);
    }, 100);
  }

  private refresh(url: string): void {
    appStore
      .loadAppInfo()
      .then(_ => {
        const pathname = window.location.pathname;
        const app = this.apps.find(x => this.matchPath(x.baseRoute, pathname) && context.hasItems(x.triggerContexts));
        this.getAppToken()
          .then((_: any) => {
            url ? this.navigate(url, false) : this.openMainMenu();
          });
      })
      .catch((_: any) => {
        this.signOut();
      });
  }

  saveToBrowserHistory(path: string, isBrowserHistoryEvent: boolean): void {
    if (isBrowserHistoryEvent) {
      window.history.replaceState(null, null, path);
    } else window.history.pushState(null, null, path);
  }

  private showLoading(show: boolean): void {
    const loadingElem = document.getElementById("Loading");

    if (show === false) {
      const hideLoading = () => loadingElem.classList.remove("show");
      const hasMounted = () => {
        const mount = document.getElementById(this._lastAppMountDomId);
        return mount && mount.firstChild && mount.firstChild.hasChildNodes();
      };

      if (hasMounted()) hideLoading();
      else {
        const interval = setInterval(() => {
          if (hasMounted()) {
            clearInterval(interval);
            hideLoading();
          }
        }, 250);
      }
    } else loadingElem.classList.add("show");
  }

  private showRelatedAppsBar(app?: AppInfo): void {
    const hideAppsBar =
      app?.id === loginAppId ||
      app?.id === jituxDashboardAppId ||
      this.getRelatedApps().length === 0 ||
      app?.appConfigurations?.enableAppsBar !== "true";
    if (hideAppsBar) document.getElementById("RelatedAppsBar").classList.add("hide");
    else document.getElementById("RelatedAppsBar").classList.remove("hide");
  }

  private getAppTokenForApp(app?: AppInfo) {
    auth.app = app;
    return auth.fetchAppToken();
  }

  private getAppToken() {
    let pathname = window.location.pathname;
    auth.app = this.apps.find(x => this.matchPath(x.baseRoute, pathname) && context.hasItems(x.triggerContexts));
    return auth.fetchAppToken();
  }

  private signIn(): void {
    const mfaKey = "mfa";
    const pathname = window.location.pathname;

    const authenticate = () => {
      const params = new URLSearchParams(window.location.search);
      let mfa = window.localStorage.getItem(mfaKey) || "true";
      if (params.has(mfaKey)) {
        mfa = params.get(mfaKey);
        window.localStorage.setItem(mfaKey, mfa);
      }

      if (auth.hasSignInError) {
        this.trackEvent("SignInError", "Unknown");
        auth.redirectToAuthStatusPage();
      }
      else if (auth.hasAuthCode) {
        auth.authenticateAuthCode();
      }
      else if (auth.hasSsoToken || mfa !== "false") {
        auth.redirectToAuthService();
      }
      else {
        auth.postLoginRedirect = window.location.href;
        this.trackEvent("NonMfaLogin");
        this.refresh("");
      }
    };

    auth
      .checkAuthentication()
      .then((authenticated: boolean) => {
        if (authenticated) {                    
          this.refresh(pathname);
        }
        else {
          this.trackEvent("UserNotAuthenticated", { pathname });
          if (pathname == this._baseHref || auth.hasSignInError || auth.hasSsoToken || auth.hasAuthCode) {
            authenticate();
          } else {
            appStore
              .loadAppInfo()
              .then(_ => {
                const app = this.apps.find(x => this.matchPath(x.baseRoute, pathname) && context.hasItems(x.triggerContexts));                
                if (app && this._currentApp !== app) {
                  this.openApp(app.id, false);
                } else {
                  authenticate();
                }
              })
              .catch(_ => {
                authenticate();
              });
          }
        }
      })
      .catch(e => {
        this.trackError("AuthenticationError", { error: e.toString() });
        console.error("Unexpected error occurred while autheticating.", e);
        throw "Unexpected error occurred while autheticating.";
      });
  }

  private signOut(): void {
    this.trackEvent("UserSignOutButton");
    auth.signOut();
  }

  private unmount(app: AppInfo): void {
    if (this._unmounts && typeof this._unmounts[app.id] == "function") {
      this.trackEvent("AppUnmounted", {
        appId: app.id,
        appName: app.displayName || app.id
      });

      this._unmounts[app.id]();
      delete this._unmounts[app.id];
    }

    const path = window.location.pathname;
    if (this.matchPath(app.baseRoute, path)) app.lastVisitedRoute = path;
  }

  private handleUnexpectedError(detail: string) {
    messageHandler.handle({
      type: UserMessageType.Error,
      message: UserMessages.UNEXPECTED_ERROR,
      detail: detail
    });
  }

  private handleErrorOpeningApp(detail: string) {
    messageHandler.handle({
      type: UserMessageType.Error,
      message: UserMessages.ERROR_OPENING_PAGE,
      detail: detail
    });
  }
}

export const shell = new Shell();